import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, doc, getDoc, updateDoc, increment, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { PublicListing, Listing, FileData, BuyerInfo } from '../types';
import './PublicSubmission.css';
 
const PublicSubmission: React.FC = () => {
  const { uniqueCode } = useParams<{ uniqueCode: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [publicListing, setPublicListing] = useState<PublicListing | null>(null);
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [checkingParsing, setCheckingParsing] = useState(false);

  // Form state
  const [offerFiles, setOfferFiles] = useState<File[]>([]);
  const [buyerInfo, setBuyerInfo] = useState<BuyerInfo>({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const fetchPublicListing = async () => {
      if (!uniqueCode) {
        setError('Invalid submission link');
        setLoading(false);
        return;
      }

      // Require authentication before loading
      if (!currentUser) {
        // Redirect to login with return URL
        navigate(`/login?returnUrl=/submit/${uniqueCode}`);
        return;
      }

      try {
        // Find the public listing by unique code
        const publicListingsQuery = query(
          collection(db, 'publicListings'),
          where('uniqueCode', '==', uniqueCode),
          where('isActive', '==', true)
        );
        const publicListingsSnapshot = await getDocs(publicListingsQuery);

        if (publicListingsSnapshot.empty) {
          setError('This submission link is invalid or has been deactivated');
          setLoading(false);
          return;
        }

        const publicListingDoc = publicListingsSnapshot.docs[0];
        const publicListingData = {
          id: publicListingDoc.id,
          ...publicListingDoc.data(),
          createdAt: publicListingDoc.data().createdAt?.toDate()
        } as PublicListing;

        setPublicListing(publicListingData);

        // Fetch the actual listing details
        const listingDoc = await getDoc(doc(db, 'listings', publicListingData.listingId));
        if (listingDoc.exists()) {
          setListing({
            id: listingDoc.id,
            ...listingDoc.data(),
            createdAt: listingDoc.data().createdAt?.toDate(),
            updatedAt: listingDoc.data().updatedAt?.toDate()
          } as Listing);
        } else {
          setError('Listing not found');
        }
      } catch (err) {
        console.error('Error fetching public listing:', err);
        setError('Failed to load submission page. Please ensure you are logged in and have permission to access this link.');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueCode, currentUser]);

  const handleOfferFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate all files are PDFs
    const nonPdfFiles = files.filter(file => file.type !== 'application/pdf');
    if (nonPdfFiles.length > 0) {
      setError('All files must be PDF format');
      return;
    }

    // Validate file sizes
    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Each file must be less than 10MB');
      return;
    }

    // Limit to 2 files
    if (files.length > 2) {
      setError('Please upload a maximum of 2 PDF files');
      return;
    }

    setError(null);
    setOfferFiles(files);
  };  const handleBuyerInfoChange = (field: keyof BuyerInfo, value: string) => {
    setBuyerInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      setError('You must be logged in to submit an offer');
      navigate('/login', { state: { from: `/submit/${uniqueCode}` } });
      return;
    }

    if (offerFiles.length === 0) {
      setError('Please upload at least one PDF (Pre-Approval and/or Purchase Agreement)');
      return;
    }

    if (!buyerInfo.name || !buyerInfo.email || !buyerInfo.phone) {
      setError('Please fill in all buyer information fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      console.log('Starting submission process...');
      
      // Create a unique folder for this submission
      const submissionTimestamp = Date.now();
      const folderPath = `offers/${publicListing?.listingId}/${submissionTimestamp}`;
      console.log('Upload path:', folderPath);

      // Upload all offer files
      console.log(`Uploading ${offerFiles.length} offer file(s)...`);
      const offerFileDataArray: FileData[] = [];
      
      for (const file of offerFiles) {
        const fileRef = ref(storage, `${folderPath}/${file.name}`);
        const fileMetadata = {
          customMetadata: {
            listingOwnerId: publicListing!.userId,
            buyerAgentId: currentUser.uid,
            submissionTimestamp: submissionTimestamp.toString()
          }
        };
        await uploadBytes(fileRef, file, fileMetadata);
        const fileUrl = await getDownloadURL(fileRef);

        offerFileDataArray.push({
          filePath: fileUrl,
          fileName: file.name,
          fileUploadedDate: new Date(),
          processed: false
        });
      }
      console.log('All files uploaded successfully');

      // Get current user profile for buyer's agent info
      console.log('Fetching user profile...');
      const userProfileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
      const userProfile = userProfileDoc.exists() ? userProfileDoc.data() : null;
      console.log('User profile fetched:', userProfile ? 'exists' : 'not found');

      // Create offer document
      console.log('Creating offer document...');
      const offerData = {
        userId: publicListing!.userId, // Listing agent's ID
        listingId: publicListing!.listingId,
        submissionDate: Timestamp.now(),
        files: offerFileDataArray,
        parsingStatus: 'pending',
        processingCompleted: false,
        ptcScore: null,
        submittedVia: 'shared-link',
        buyerInfo: buyerInfo, // Private buyer information
        buyerAgentId: currentUser.uid,
        buyerAgentName: `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim(),
        buyerAgentEmail: currentUser.email || ''
      };

      const offerRef = await addDoc(collection(db, 'offers'), offerData);
      console.log('Offer created successfully:', offerRef.id);

      // Increment submission count on public listing
      console.log('Updating submission count...');
      if (publicListing?.id) {
        await updateDoc(doc(db, 'publicListings', publicListing.id), {
          submissionCount: increment(1)
        });
      }
      console.log('Submission count updated');

      setSubmissionId(offerRef.id);
      setSuccess(true);
      
      // Poll for parsing status (check every 2 seconds for up to 30 seconds)
      setCheckingParsing(true);
      let attempts = 0;
      const maxAttempts = 15;
      const checkInterval = setInterval(async () => {
        attempts++;
        try {
          const offerDoc = await getDoc(doc(db, 'offers', offerRef.id));
          const offerStatus = offerDoc.data();
          
          if (offerStatus?.processingCompleted) {
            clearInterval(checkInterval);
            setCheckingParsing(false);
            
            if (offerStatus.parsingStatus === 'failed' && offerStatus.parsingError) {
              setParsingError(offerStatus.parsingError);
            }
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            setCheckingParsing(false);
          }
        } catch (pollErr) {
          console.error('Error checking parsing status:', pollErr);
        }
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting offer:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      setError(`Failed to submit offer: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="public-submission-container">
        <div className="submission-card">
          <div className="submission-header">
            <div className="logo-section">
              <div className="star-icon">★</div>
              <h1>Score My Offer</h1>
            </div>
          </div>
          <div className="auth-required">
            <h2>Authentication Required</h2>
            <p>You need to be logged in as a buyer's agent to submit an offer.</p>
            <button 
              className="btn-primary"
              onClick={() => navigate('/login', { state: { from: `/submit/${uniqueCode}` } })}
            >
              Login to Continue
            </button>
            <p className="signup-link">
              Don't have an account? <a href="/signup">Sign up here</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="public-submission-container">
        <div className="submission-card">
          <div className="loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  if (error && !listing) {
    return (
      <div className="public-submission-container">
        <div className="submission-card">
          <div className="error-state">
            <h2>⚠️ Error</h2>
            <p>{error}</p>
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="public-submission-container">
        <div className="submission-card">
          <div className="success-state">
            <div className="success-icon">✓</div>
            <h2>Offer Submitted Successfully!</h2>
            <p>Your offer has been submitted and is being processed.</p>
            <div className="submission-details">
              <p><strong>Submission ID:</strong> {submissionId}</p>
              <p><strong>Property:</strong> {listing?.address}</p>
              <p><strong>Submitted:</strong> {new Date().toLocaleString()}</p>
            </div>
            
            {checkingParsing && (
              <div className="processing-status">
                <p>⏳ Processing uploaded documents...</p>
              </div>
            )}
            
            {parsingError && (
              <div className="warning-message" style={{ 
                backgroundColor: '#fef3c7', 
                border: '2px solid #f59e0b',
                padding: '1rem',
                borderRadius: '8px',
                marginTop: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#92400e', marginTop: 0 }}>⚠️ Document Processing Issue</h3>
                <p style={{ color: '#92400e', marginBottom: '0.5rem' }}>{parsingError}</p>
                <p style={{ color: '#92400e', fontSize: '0.9rem', marginBottom: 0 }}>
                  <strong>Action Required:</strong> Please upload text-based PDF files. If you only have scanned PDFs, 
                  use OCR (Optical Character Recognition) software to convert them first, or contact the listing agent 
                  directly with your offer documents.
                </p>
              </div>
            )}
            
            {!parsingError && !checkingParsing && (
              <p className="confirmation-note">
                You will receive a confirmation email shortly with your submission details.
              </p>
            )}
            
            <button className="btn-primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-submission-container">
      <div className="submission-card">
        <div className="submission-header">
          <div className="logo-section">
            <div className="star-icon">★</div>
            <h1>Score My Offer</h1>
          </div>
          <h2>Submit Your Offer</h2>
        </div>

        {listing && (
          <div className="property-info">
            <h3>Property Details</h3>
            <div className="property-address">
              <strong>{listing.address}</strong>
              {listing.city && listing.state && (
                <span className="property-location">
                  {listing.city}, {listing.state} {listing.zipCode}
                </span>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="submission-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-section">
            <h3>Offer Documents *</h3>
            <p className="field-description">
              Upload 1-2 PDF files containing:
              <br />• Pre-Approval Letter (required)
              <br />• Purchase Agreement (optional - can be submitted later)
              <br /><br />
              You can upload a single combined PDF or separate PDFs. Our system will automatically detect and extract information from each document.
            </p>
            <div className="file-upload-wrapper">
              <input
                type="file"
                id="offerFiles"
                accept="application/pdf"
                multiple
                onChange={handleOfferFilesChange}
                className="file-input"
                required
              />
              <label htmlFor="offerFiles" className="file-input-label">
                {offerFiles.length > 0 
                  ? `✓ ${offerFiles.length} file(s) selected` 
                  : '📄 Choose PDF File(s) (Max 2)'}
              </label>
            </div>
            {offerFiles.length > 0 && (
              <div className="files-selected">
                {offerFiles.map((file, idx) => (
                  <div key={idx} className="file-selected">
                    ✓ {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Buyer Information *</h3>
            <p className="field-description privacy-note">
              🔒 This information is private and will not be shared with the listing agent. 
              It is used only for offer scoring purposes.
            </p>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="buyerName">Buyer's Full Name</label>
                <input
                  type="text"
                  id="buyerName"
                  value={buyerInfo.name}
                  onChange={(e) => handleBuyerInfoChange('name', e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="buyerEmail">Buyer's Email</label>
                <input
                  type="email"
                  id="buyerEmail"
                  value={buyerInfo.email}
                  onChange={(e) => handleBuyerInfoChange('email', e.target.value)}
                  placeholder="buyer@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="buyerPhone">Buyer's Phone</label>
                <input
                  type="tel"
                  id="buyerPhone"
                  value={buyerInfo.phone}
                  onChange={(e) => handleBuyerInfoChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-submit"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Offer'}
            </button>
          </div>

          <p className="submission-note">
            By submitting this offer, you confirm that all information and documents are accurate and complete.
          </p>
        </form>
      </div>
    </div>
  );
};

export default PublicSubmission;
